import { type Simplify } from 'type-fest'

type PropUpdated<T extends object, K extends keyof T, U = T[K]> = Simplify<
  Omit<T, K> & {
    [key in K]: U
  }
>

/**
 * オブジェクトの指定したプロパティの値を更新します。
 *
 * @template T - オブジェクトの型。
 * @template K - キーの型。
 * @template U - 更新後の値の型。
 * @param obj - オブジェクト。
 * @param key - キー。
 * @param fn - プロパティの値とオブジェクトを引数に取り、更新後の値を返す関数。
 * @returns 更新後のオブジェクト。
 */
export function updateProp<T extends object, K extends keyof T, U = T[K]>(
  obj: T,
  key: K,
  fn: (value: T[K], obj: T) => U,
): PropUpdated<T, K, U>

/**
 * オブジェクトの指定したプロパティの値を更新します。
 *
 * @template T - オブジェクトの型。
 * @template K - キーの型。
 * @template U - 更新後の値の型。
 * @param key - キー。
 * @param fn - プロパティの値とオブジェクトを引数に取り、更新後の値を返す関数。
 * @returns オブジェクトを引数に取り、更新後のオブジェクトを返す関数。
 */
export function updateProp<T extends object, K extends keyof T, U = T[K]>(
  key: K,
  fn: (value: T[K], obj: T) => U,
): (obj: T) => PropUpdated<T, K, U>

/**
 * @param args - 引数。
 * @returns 結果または部分適用された関数。
 */
export function updateProp<T extends object, K extends keyof T, U = T[K]>(
  ...args: [K, (value: T[K], obj: T) => U] | [T, K, (value: T[K], obj: T) => U]
): PropUpdated<T, K, U> | ((obj: T) => PropUpdated<T, K, U>) {
  const updatePropInner = (
    obj: T,
    key: K,
    fn: (value: T[K], obj: T) => U,
  ): PropUpdated<T, K, U> => ({
    ...obj,
    [key]: fn(obj[key], obj),
  })

  return args.length === 3
    ? updatePropInner(...(args as [T, K, (value: T[K], obj: T) => U]))
    : (obj) =>
        updatePropInner(obj, ...(args as [K, (value: T[K], obj: T) => U]))
}
